import React, { useMemo, useState } from "react";
import { FieldValues, UseFormReturn } from "react-hook-form";
import { z } from "zod";
import FormTemplate from "../../templates/FormTemplate";
import styled from "styled-components";
import { Input } from "../../atoms/Input";
import { Button } from "../../atoms/Button";
import { contactFormApi } from "../../../core/http/httpClient";
import { useLanguage } from '../../../contexts/LanguageContext';

type Props = {
  type?: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  phoneNumber?: string;
  message?: string;
  handleSubmit?: (
    firstName: string,
    lastName: string,
    email: string,
    phoneNumber: string,
    message: string
  ) => void;
  isLoading?: boolean;
  onSuccess?: () => void;
  onError?: () => void;
  setErrorTrigger?: (trigger: () => void) => void;
  setSuccessTrigger?: (trigger: () => void) => void;
};

export const ContactForm: React.FC<Props> = ({
  type,
  firstName,
  lastName,
  email,
  phoneNumber,
  message,
  handleSubmit,
  isLoading = false,
  onSuccess,
  onError,
  setErrorTrigger,
  setSuccessTrigger,
}) => {
  const { t } = useLanguage();
  const [methods, setMethods] = useState<UseFormReturn<FieldValues, any, undefined> | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{ type: 'success' | 'error', message: string } | null>(null);

  const schema = useMemo(() => {
    return z.object({}).extend({
      firstName: z.string().min(1, t('forms.errors.firstNameRequired')),
      lastName: z.string().optional(),
      email: z.string().email(t('forms.errors.emailInvalid')).optional().or(z.literal("")),
      phoneNumber: z.string().min(1, t('forms.errors.phoneNumberRequired')),
      message: z.string().optional(),
    });
  }, [t]);

  // Function to handle successful API response
  const handleApiSuccess = React.useCallback(() => {
    setSubmitMessage({
      type: 'success',
      message: t('forms.messages.contactSuccess')
    });
    setIsSubmitting(false);

    // Reset form with empty values immediately
    if (methods) {
      methods.reset({
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        message: '',
      });
      methods.clearErrors();
    }

    onSuccess?.();
  }, [methods, onSuccess, t]);

  // Function to handle failed API response
  const handleApiError = React.useCallback((errorMessage?: string) => {
    setSubmitMessage({
      type: 'error',
      message: errorMessage || t('forms.messages.contactGenericError')
    });
    setIsSubmitting(false);
    onError?.();
  }, [onError, t]);

  // Register the trigger functions with parent (for backward compatibility)
  React.useEffect(() => {
    if (setErrorTrigger) {
      setErrorTrigger(handleApiError);
    }
    if (setSuccessTrigger) {
      setSuccessTrigger(handleApiSuccess);
    }
  }, [setErrorTrigger, setSuccessTrigger, handleApiError, handleApiSuccess]);

  const handleFormSubmit = async (data: any) => {
    // Clear any previous submit message when starting a new submission
    setSubmitMessage(null);

    // Don't proceed if already submitting (prevent double submission)
    if (isSubmitting) {
      return;
    }

    setIsSubmitting(true);

    try {
      // If parent handleSubmit is provided, use it (for backward compatibility)
      if (handleSubmit) {
        handleSubmit(
          data.firstName,
          data.lastName || '',
          data.email || '',
          data.phoneNumber,
          data.message || ''
        );
        return;
      }

      // Handle API call directly like InquiryForm
      const requestBody = {
        first_name: data.firstName,
        last_name: data.lastName || '',
        email: data.email || '',
        phone_number: data.phoneNumber,
        message: data.message || '',
      };

      await contactFormApi.call(requestBody);

      // Handle success
      handleApiSuccess();
    } catch (error: any) {
      console.error('Error submitting contact form:', error);
      const errorMessage = error.response?.data?.message || "Sorry! Something went wrong. Please try again or contact us directly.";
      handleApiError(errorMessage);
    }
  };

  // Clear submit message when user starts typing again
  const handleInputChange = () => {
    if (submitMessage) {
      setSubmitMessage(null);
    }
  };

  return (
    <FormTemplate
      schema={schema}
      setMethods={setMethods as (method: UseFormReturn<FieldValues, any, undefined>) => void}
      defaultValues={{
        firstName: '',
        lastName: '',
        email: '',
        phoneNumber: '',
        message: '',
      }}
      onSubmit={handleFormSubmit}
    >
      <ContentWrapper>
        <Row>
          <InputContainer>
            <Input
              name="firstName"
              label={t('forms.fields.firstName')}
              placeholder={t('forms.placeholders.firstName')}
              type="text"
              required
              height={"48px"}
              onChange={handleInputChange}
            />
          </InputContainer>
          <InputContainer>
            <Input
              name="lastName"
              label={t('forms.fields.lastName')}
              placeholder={t('forms.placeholders.lastName')}
              type="text"
              height={"48px"}
              onChange={handleInputChange}
            />
          </InputContainer>
        </Row>
        <InputContainer>
          <Input
            name="email"
            label={t('forms.fields.email')}
            placeholder={t('forms.placeholders.email')}
            type="email"
            height={"48px"}
            onChange={handleInputChange}
          />
        </InputContainer>
        <InputContainer>
          <Input
            name="phoneNumber"
            label={t('forms.fields.phoneNumber')}
            placeholder={t('forms.placeholders.phoneNumber')}
            type="tel"
            required
            height={"48px"}
            onChange={handleInputChange}
          />
        </InputContainer>
        <InputContainer>
          <Input
            name="message"
            label={t('forms.fields.message')}
            placeholder={t('forms.placeholders.message')}
            type="textarea"
            width={"100%"}
            onChange={handleInputChange}
          />
        </InputContainer>
        {submitMessage && (
          <MessageWrapper type={submitMessage.type}>
            {submitMessage.message}
          </MessageWrapper>
        )}
        <ButtonWrapper>
          <Button
            text={isSubmitting ? t('forms.buttons.submitting') : t('forms.buttons.submit')}
            type="submit"
            backgroundColor="#06163A"
            textColor="#fff"
            width="140px"
            height="36px"
            disabled={isSubmitting}
          />
        </ButtonWrapper>
      </ContentWrapper>
    </FormTemplate>
  )
}

const ContentWrapper = styled.div`
  margin: 0 0 30px;
  display: flex;
  flex-direction: column;
  gap: 18px;
  @media (max-width: 900px) {
    gap: 14px;
  }
`;

const Row = styled.div`
  display: flex;
  flex-direction: row;
  gap: 16px;
  > * {
    flex: 1 1 0;
  }
  @media (max-width: 900px) {
    flex-direction: column;
    gap: 14px;
  }
`;

const InputContainer = styled.div`
    margin: 0;
 @media (max-width: 900px) {
    margin: 0;
  }
    
`

const ButtonWrapper = styled.div`
  display: flex;
  justify-content: center;
  margin: 0 16px;
  button {
    height: 48px;
    background: #06163A;
    color: #fff;
    font-weight: 600;
    font-size: 16px;
    border-radius: 6px;
    padding: 12px 0;
  }
  @media (max-width: 900px) {
    button {
      font-size: 15px;
      height: 44px;
    }
  }
`;

const MessageWrapper = styled.div<{ type: 'success' | 'error' }>`
  background: ${props => props.type === 'success' ? '#d4edda' : '#f8d7da'};
  color: ${props => props.type === 'success' ? '#155724' : '#721c24'};
  border: 1px solid ${props => props.type === 'success' ? '#c3e6cb' : '#f5c6cb'};
  border-radius: 6px;
  padding: 12px 16px;
  margin: 0 16px;
  font-size: 14px;
  font-weight: 500;
  text-align: center;
  animation: fadeIn 0.3s ease-in;
  
  @keyframes fadeIn {
    from {
      opacity: 0;
      transform: translateY(-10px);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
`;